import { WebAPIClient, WebContainer } from "@skygear/web";
import { ApolloClient } from "apollo-boost";
import { InMemoryCache } from "apollo-cache-inmemory";
import { HttpLink } from "apollo-link-http";
import { PresignRequestHeader } from "../generated/graphql";

interface ContainerConfig {
  apiKey: string;
  endpoint: string;
}

export class APIClient<T extends WebAPIClient> {
  private skygearContainer: WebContainer<T>;
  private skygearConfig: ContainerConfig | null = null;
  apolloClient: ApolloClient<any>;

  constructor(orderToken: string) {
    this.skygearContainer = new WebContainer<T>();
    this.apolloClient = new ApolloClient({
      link: new HttpLink({
        headers: {
          "x-order-token": orderToken,
        },
        uri: "/api/portalapi/graphql",
        fetch: (input, init?) => this.proxyGraphQLFetchToSkygear(input, init),
      }),
      cache: new InMemoryCache(),
      defaultOptions: {
        query: {
          fetchPolicy: "network-only",
        },
        mutate: {
          fetchPolicy: "network-only",
        },
        watchQuery: {
          fetchPolicy: "network-only",
        },
      },
    });
  }

  configure = async (options: ContainerConfig): Promise<APIClient<T>> => {
    this.skygearConfig = options;
    await this.skygearContainer.configure(this.skygearConfig);
    return this;
  };

  private async proxyGraphQLFetchToSkygear(
    input: RequestInfo,
    init?: RequestInit
  ): Promise<Response> {
    if (typeof input !== "string") {
      throw new Error("Only support input as string");
    }
    return this.skygearContainer.fetch(input, init);
  }

  async uploadAssets(
    assetBlob: Blob,
    assetId: string,
    url: string,
    method: string,
    headers: PresignRequestHeader[],
    onUploadProgress?: (e: ProgressEvent) => void
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const client = new XMLHttpRequest();
      client.upload.onprogress = pe => {
        if (onUploadProgress) {
          onUploadProgress(pe);
        }
      };
      client.upload.onload = () => {
        resolve(assetId);
      };
      client.onerror = () => {
        reject(new Error("on-client-error"));
      };
      client.ontimeout = () => {
        reject(new Error("on-client-timeout"));
      };
      client.open(method, url);
      for (const header of headers) {
        client.setRequestHeader(header.key, header.value);
      }
      client.send(assetBlob);
    });
  }
}
